/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { CloseOutlined, Loading3QuartersOutlined } from "@ant-design/icons";
import { Typography, Tabs, Button, Spin, DatePicker } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../config/hooks";
import { processLatLngs, removeLatLngs } from "../../ducks/actions";
import CustomDayHeader from "../CustomDayHeader";
import RenderEventContent from "../RenderEventContent";
import { CalendarIcon } from "../../../../atoms/CustomIcons";
import ScheduleIcon from "../../../../atoms/CustomIcons/ScheduleIcon";
import DateNavigatorLeft from "../../../../atoms/CustomIcons/DateNavigatorLeft";
import DateNavigatorRight from "../../../../atoms/CustomIcons/DateNavigatorRight";

dayjs.extend(duration);
const antIcon = <Loading3QuartersOutlined spin />;

interface Coordinates {
  lat: number;
  lng: number;
}

interface DriveTimeDataApiItem {
  from: string;
  to: string;
  duration_seconds: number;
  duration_text: string;
}

interface VisitExtendedProps {
  type: "visit" | "drive";
  durationMinutes?: number;
  driveTimeMinutes?: number;
  location?: string;
  nextVisitLocation?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
}

interface FullCalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  date?: string;
  allDay?: boolean;
  editable?: boolean;
  origin?: Coordinates;
  extendedProps?: VisitExtendedProps;
}

const generateEventsWithDriveTimes = async (
  initialVisitEvents: FullCalendarEvent[],
  driveTimeData: DriveTimeDataApiItem[]
): Promise<FullCalendarEvent[]> => {
  const processedEvents: FullCalendarEvent[] = [];

  const sortedVisitEvents = [...initialVisitEvents].sort((a, b) =>
    dayjs(a.start).diff(dayjs(b.start))
  );

  let currentEffectiveEndTime: dayjs.Dayjs | null = null;

  for (let i = 0; i < sortedVisitEvents.length; i++) {
    const currentVisit = { ...sortedVisitEvents[i] };
    if (
      currentEffectiveEndTime &&
      dayjs(currentVisit.start).isBefore(currentEffectiveEndTime)
    ) {
      const originalDuration = dayjs(currentVisit.end).diff(
        dayjs(currentVisit.start)
      );
      currentVisit.start = currentEffectiveEndTime.toISOString();
      currentVisit.end = dayjs(currentEffectiveEndTime)
        .add(originalDuration, "millisecond")
        .toISOString();
    }

    processedEvents.push(currentVisit);
    currentEffectiveEndTime = dayjs(currentVisit.end);

    if (i < sortedVisitEvents.length - 1) {
      const nextVisit = sortedVisitEvents[i + 1];

      const isSameCalendarDay = dayjs(currentVisit.end).isSame(
        dayjs(nextVisit.start),
        "day"
      );

      if (currentVisit.origin && nextVisit.origin && isSameCalendarDay) {
        const fromCoordString = `${currentVisit.origin.lat},${currentVisit.origin.lng}`;
        const toCoordString = `${nextVisit.origin.lat},${nextVisit.origin.lng}`;

        const foundDriveTime = driveTimeData.find(
          (data) => data.from === fromCoordString && data.to === toCoordString
        );

        if (foundDriveTime && foundDriveTime.duration_seconds > 0) {
          const driveStart = currentEffectiveEndTime;
          const duration = foundDriveTime.duration_seconds + 400;
          // if (foundDriveTime.duration_seconds < 500) {
          //   duration = foundDriveTime.duration_seconds + 500;
          // } else {
          //   duration = foundDriveTime.duration_seconds;
          // }
          const driveEnd = driveStart.add(duration, "second");
          processedEvents.push({
            id: `drive-${currentVisit.id}-to-${nextVisit.id}`,
            title: `Drive to ${
              nextVisit.extendedProps?.location || "next location"
            }`,
            start: driveStart.toISOString(),
            end: driveEnd.toISOString(),
            editable: false,
            extendedProps: {
              type: "drive",
              driveTimeMinutes: Math.ceil(duration / 60),
              location: currentVisit.extendedProps?.location,
              nextVisitLocation: nextVisit.extendedProps?.location,
              backgroundColor: "#E3F2FD",
              borderColor: "#2196F3",
              textColor: "#0D47A1",
            },
          });
          currentEffectiveEndTime = driveEnd;
        }
      }
    }
  }
  return processedEvents;
};

export default function ScheduleWrapper({ crewData, onClose }: any) {
  const [hoursData, setHoursData] = useState<HourData[] | null>(null);

  const calendarRef = useRef<any>(null);
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const [selectedCrewId, setSelectedCrewId] = useState<string | undefined>(
    undefined
  );
  const [visitEvents, setVisitEvents] = useState<FullCalendarEvent[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<FullCalendarEvent[]>([]);
  const [currentWeekRange, setCurrentWeekRange] = useState("");

  const handleDatesSet = (arg: any) => {
    const start = dayjs(arg.start);
    const end = dayjs(arg.end).subtract(1, "day"); // FullCalendar includes the next day for week view

    const formatted =
      start.format("MMM D") +
      " – " +
      (start.month() === end.month() ? end.format("D") : end.format("D"));

    setCurrentWeekRange(formatted);
  };

  const dispatch = useAppDispatch();
  const driveTimeData = useAppSelector(
    (state) => state?.schedule?.driveTimeData?.data || []
  );
  const driveTimeLoader = useAppSelector(
    (state) => state?.schedule?.driveTimeData?.loading || false
  );

  useEffect(() => {
    if (crewData && crewData.length > 0) {
      if (selectedCrewId === undefined) {
        setSelectedCrewId(crewData?.[0].id);
      }

      const currentCrew = crewData.find(
        (crew: any) => crew.id === (selectedCrewId || crewData?.[0].id)
      );

      if (currentCrew) {
        const mappedVisits: FullCalendarEvent[] = currentCrew.visit.map(
          (visit: any) => ({
            id: visit.id,
            title: visit.title,
            start: visit.start,
            end: visit.end,
            origin: visit.origin,
            editable: true,
            extendedProps: {
              type: "visit",
              location: visit.address,
              backgroundColor: "#E8F5E9",
              borderColor: "#4CAF50",
              textColor: "#1B5E20",
            },
            workOrders: visit?.workOrders || [],
            homeIcon: visit?.homeIcon || "",
          })
        );
        setVisitEvents(mappedVisits);
      } else {
        setVisitEvents([]);
      }
    } else {
      setSelectedCrewId(undefined);
      setVisitEvents([]);
    }
  }, [crewData, selectedCrewId]);

  const triggerProcessLatLngs = useCallback(() => {
    dispatch(removeLatLngs());
    if (visitEvents.length > 0) {
      const coordinatesToProcess: Coordinates[] = visitEvents
        .filter((event) => event.origin !== undefined)
        .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)))
        .map((event) => {
          if (event.origin === undefined) {
            throw new Error("Event origin is undefined");
          }
          return event.origin;
        });

      const mappedCoordinates = coordinatesToProcess.map((coord) => ({
        lat: coord.lat,
        lng: coord.lng,
      }));

      if (mappedCoordinates.length > 1) {
        const payload = {
          coordinates: mappedCoordinates,
        };
        dispatch(processLatLngs(payload));
      } else {
        console.log(
          "Not enough coordinates to calculate drive times for current crew."
        );

        setCalendarEvents(visitEvents);
      }
    }
  }, [dispatch, visitEvents]);

  useEffect(() => {
    triggerProcessLatLngs();
  }, [triggerProcessLatLngs]);

  interface HourData {
    date: string;
    hours: number; // Formatted as "Xh Ym"
  }

  const calculateDailyHours = (events: FullCalendarEvent[]): HourData[] => {
    const dailyTotalMinutes: { [date: string]: number } = {};

    events.forEach((event) => {
      // Ensure event has valid start and end times
      if (event.start && event.end) {
        const start = dayjs(event.start);
        const end = dayjs(event.end);

        // Only consider events that have a valid duration and are not "drive" events
        // If you want to include drive times in the total, remove the `&& event.extendedProps?.type !== "drive"` condition
        if (start.isValid() && end.isValid() && start.isBefore(end)) {
          const eventDurationMinutes = end.diff(start, "minute");
          const eventDate = start.format("YYYY-MM-DD"); // Get date in YYYY-MM-DD format

          // Add duration to the total for that day
          dailyTotalMinutes[eventDate] =
            (dailyTotalMinutes[eventDate] || 0) + eventDurationMinutes;
        }
      }
    });

    // Convert the daily total minutes into the desired 'hourData' format
    const hourData: HourData[] = Object.keys(dailyTotalMinutes).map((date) => {
      const totalMinutes = dailyTotalMinutes[date];

      return {
        date: date,
        hours: totalMinutes, // Trim to remove leading/trailing spaces if only one part exists
      };
    });

    // Sort the results by date
    return hourData.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
  };

  useMemo(() => {
    if (Array.isArray(driveTimeData) && visitEvents?.length > 0) {
      const generateAndSetCalendarEvents = async () => {
        const generated = await generateEventsWithDriveTimes(
          visitEvents,
          driveTimeData as DriveTimeDataApiItem[]
        );

        const dailyHours = calculateDailyHours(generated);

        setCalendarEvents(generated);
        setHoursData(dailyHours);
      };
      generateAndSetCalendarEvents();
    }
  }, [visitEvents, driveTimeData]);

  // console.log("Generated Calendar Events:", hoursData);

  // const handleEventDrop = useCallback(async (info: any) => {
  //   const { event } = info;
  //   console.log(
  //     "Event dropped:",
  //     event,
  //     "New start:",
  //     event.start,
  //     "New end:",
  //     event.end
  //   );

  //   setVisitEvents((prevVisitEvents) => {
  //     const updatedEvents = prevVisitEvents.map((e) => {
  //       if (e.id === event.id) {
  //         return {
  //           ...e,
  //           start: event.start.toISOString(),
  //           end: event.end.toISOString(),
  //         };
  //       }
  //       return e;
  //     });
  //     return updatedEvents.sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));
  //   });
  // }, []);

  // const handleEventDrop = useCallback(
  //   (info: any) => {
  //     const { event } = info;

  //     const movedEventId = event.id;
  //     const newStart = dayjs(event.start);
  //     const newEnd = dayjs(event.end);
  //     const newDate = newStart.format("YYYY-MM-DD");

  //     const oldEvent = visitEvents.find((e) => e.id === movedEventId);
  //     if (!oldEvent) return;

  //     const oldDate = dayjs(oldEvent.start).format("YYYY-MM-DD");

  //     const updatedMovedEvent: FullCalendarEvent = {
  //       ...oldEvent,
  //       start: newStart.toISOString(),
  //       end: newEnd.toISOString(),
  //     };

  //     // Events remaining on the OLD day (without the moved one)
  //     const oldDayEvents = visitEvents
  //       .filter(
  //         (e) =>
  //           dayjs(e.start).format("YYYY-MM-DD") === oldDate &&
  //           e.id !== movedEventId &&
  //           e.extendedProps?.type === "visit"
  //       )
  //       .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

  //     // Events on the NEW day (plus the moved one if changing date)
  //     const newDayEvents = visitEvents
  //       .filter(
  //         (e) =>
  //           dayjs(e.start).format("YYYY-MM-DD") === newDate &&
  //           e.id !== movedEventId &&
  //           e.extendedProps?.type === "visit"
  //       )
  //       .concat(updatedMovedEvent)
  //       .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

  //     // Recalculate time slots starting from 7:00 AM
  //     const adjustTimeline = (
  //       events: FullCalendarEvent[],
  //       day: string
  //     ): FullCalendarEvent[] => {
  //       let current = dayjs(`${day}T07:00:00`);
  //       return events.map((e) => {
  //         const duration = dayjs(e.end).diff(dayjs(e.start), "minute");
  //         const start = current;
  //         const end = start.add(duration, "minute");
  //         current = end;
  //         return {
  //           ...e,
  //           start: start.toISOString(),
  //           end: end.toISOString(),
  //         };
  //       });
  //     };

  //     const adjustedOldDay = adjustTimeline(oldDayEvents, oldDate);
  //     const adjustedNewDay = adjustTimeline(newDayEvents, newDate);

  //     // Build final event list
  //     const otherEvents = visitEvents.filter(
  //       (e) =>
  //         dayjs(e.start).format("YYYY-MM-DD") !== oldDate &&
  //         dayjs(e.start).format("YYYY-MM-DD") !== newDate
  //     );

  //     const finalEvents = [
  //       ...otherEvents,
  //       ...adjustedOldDay,
  //       ...adjustedNewDay,
  //     ].sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

  //     setVisitEvents(finalEvents);
  //   },
  //   [visitEvents]
  // );

  const handleEventDrop = useCallback(
    (info: any) => {
      const { event } = info;

      const movedEventId = event.id;
      const newStart = dayjs(event.start);
      const newEnd = dayjs(event.end);
      const newDate = newStart.format("YYYY-MM-DD");

      const oldEvent = visitEvents.find((e) => e.id === movedEventId);
      if (!oldEvent) return;

      const oldStart = dayjs(oldEvent.start);
      const oldDate = oldStart.format("YYYY-MM-DD");

      const updatedMovedEvent: FullCalendarEvent = {
        ...oldEvent,
        start: newStart.toISOString(),
        end: newEnd.toISOString(),
      };

      // Events to keep unchanged
      const otherEvents = visitEvents.filter((e) => e.id !== movedEventId);

      // Adjust OLD day events (move up after removed event)
      const oldDayEvents = otherEvents
        .filter((e) => dayjs(e.start).format("YYYY-MM-DD") === oldDate)
        .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

      const adjustedOldDay: FullCalendarEvent[] = [];
      let oldCursor = dayjs(`${oldDate}T07:00:00`);

      for (const event of oldDayEvents) {
        if (
          dayjs(event.start).isSame(oldStart) ||
          dayjs(event.start).isAfter(oldStart)
        ) {
          const duration = dayjs(event.end).diff(dayjs(event.start), "minute");
          const newStart = oldCursor;
          const newEnd = newStart.add(duration, "minute");
          adjustedOldDay.push({
            ...event,
            start: newStart.toISOString(),
            end: newEnd.toISOString(),
          });
          oldCursor = newEnd;
        } else {
          adjustedOldDay.push(event);
          oldCursor = dayjs(event.end);
        }
      }

      // Adjust NEW day events (move down if after dragged time)
      const newDayEvents = otherEvents
        .filter((e) => dayjs(e.start).format("YYYY-MM-DD") === newDate)
        .sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

      const adjustedNewDay: FullCalendarEvent[] = [];
      let newCursor = newEnd; // Start shifting from end of dropped event
      const inserted = false;

      for (const event of newDayEvents) {
        if (!inserted && dayjs(event.start).isAfter(newStart)) {
          const duration = dayjs(event.end).diff(dayjs(event.start), "minute");
          const newStartTime = newCursor;
          const newEndTime = newStartTime.add(duration, "minute");
          adjustedNewDay.push({
            ...event,
            start: newStartTime.toISOString(),
            end: newEndTime.toISOString(),
          });
          newCursor = newEndTime;
        } else {
          adjustedNewDay.push(event);
          if (dayjs(event.end).isAfter(newCursor)) {
            newCursor = dayjs(event.end);
          }
        }
      }

      // Combine all updated events
      const untouchedEvents = otherEvents.filter(
        (e) =>
          dayjs(e.start).format("YYYY-MM-DD") !== oldDate &&
          dayjs(e.start).format("YYYY-MM-DD") !== newDate
      );

      const finalEvents = [
        ...untouchedEvents,
        ...adjustedOldDay,
        updatedMovedEvent,
        ...adjustedNewDay,
      ].sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

      setVisitEvents(finalEvents);
    },
    [visitEvents]
  );

  const handleTabChange = (key: string) => {
    setSelectedCrewId(key);
  };

  const handleDateChange = (date: any) => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.gotoDate(date.format("YYYY-MM-DD"));
  };

  if (!crewData || crewData.length === 0) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography.Title level={3}>No Crew Data Available</Typography.Title>
        <Typography.Text>
          Please provide crew data to display the schedule.
        </Typography.Text>
      </div>
    );
  }

  // console.log("calendarEvents", calendarEvents);

  return (
    <Spin indicator={antIcon} size="large" spinning={driveTimeLoader}>
      <div className="scheduleWrapperCalendar">
        <Tabs
          defaultActiveKey={crewData?.[0]?.id}
          activeKey={selectedCrewId}
          onChange={handleTabChange}
          destroyOnHidden
          items={crewData?.map((crew: any) => ({
            key: crew.id,
            label: (
              <div className="crewTabLabel">
                {crew.name}
                <Button
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  className="only_icon"
                />
              </div>
            ),
            children: (
              <>
                <div
                  className="scheduleHeader calendarHeader"
                  style={{ padding: "12px 12px 4px" }}
                >
                  <div className="leftHeader">
                    <DatePicker
                      onChange={handleDateChange}
                      format={"ddd, MMM DD"}
                      allowClear={false}
                      suffixIcon={<CalendarIcon />}
                    />
                    <div className="dateWrapper">
                      <Button
                        icon={<DateNavigatorLeft />}
                        type="text"
                        className="only_icon previous_btn"
                        onClick={() => calendarRef.current.getApi().prev()}
                      />
                      <div className="datePickerWrapper">
                        <span className="date">{currentWeekRange}</span>
                      </div>
                      <Button
                        icon={<DateNavigatorRight />}
                        type="text"
                        className="only_icon next_btn"
                        onClick={() => calendarRef.current.getApi().next()}
                      />
                    </div>
                  </div>

                  <div className="rightHeader">
                    <Button
                      type="primary"
                      onClick={() =>
                        (window.location.href = `/scheduler/${companyId}`)
                      }
                    >
                      <ScheduleIcon /> Schedule work
                    </Button>
                  </div>
                </div>

                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView="timeGridWeek"
                  firstDay={1}
                  headerToolbar={false}
                  dayHeaderContent={(arg: any) => (
                    <CustomDayHeader
                      date={arg.date}
                      events={calendarEvents}
                      hoursData={hoursData}
                      crewData={crewData}
                    />
                  )}
                  slotMinTime="07:00:00"
                  slotMaxTime="22:00:00"
                  slotDuration="00:30:00"
                  slotLabelInterval="01:00"
                  expandRows={true}
                  events={calendarEvents}
                  eventContent={(e) => <RenderEventContent eventInfo={e} />}
                  allDaySlot={false}
                  editable={true}
                  eventDrop={handleEventDrop}
                  height="auto"
                  contentHeight="auto"
                  eventMinWidth={150}
                  aspectRatio={1.8}
                  dayHeaderClassNames="custom-day-header"
                  dayCellClassNames="custom-day-cell"
                  datesSet={handleDatesSet}
                  // eventOverlap={false}
                  slotEventOverlap={false}
                />
              </>
            ),
          }))}
        />
        {/* <div style={{ marginBottom: "20px", textAlign: "right" }}>
        <Button
          onClick={triggerProcessLatLngs}
          type="primary"
          style={{
            backgroundColor: "#1890ff",
            borderColor: "#1890ff",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            borderRadius: "8px",
          }}
        >
          Recalculate Drive Times for
        </Button>
      </div> */}
      </div>
    </Spin>
  );
}
